import { ConversationSearchResult } from '../../types/ConversationSearchResult';
import { ConversationFilterParams } from '../../types/ConversationFilterParams';
import api from '../api/api';
import GetConversationsConfig from '../../types/GetConversationsConfig';
import IndividualConversation from '../../types/IndividualConversation';
import MessageRating from '../../types/MessageRating';
import MessageRatingResponse from '../../types/MessageRatingResponse';
import GetIntentsConfig from '../../types/GetIntentsConfig';
import { IntentSearchResult } from '../../types/IntentSearchResult';
import ExportFileType from '../../types/ExportFileType';
import SortColumn from '../../types/SortColumn';
import PreviousNextConversation from '../../types/PreviousNextConversation';
import SortDirection from '../../types/SortDirection';
import ChatbotAnalytics from '../../types/Analytics/ChatbotAnalytics';
import ReviewTag from '../../types/ReviewTag';
import ReviewComment from '../../types/ReviewComment';
import Issue, { IssueScope } from '../../types/Issue';

class AnalyticsService {
  static async getConversations({
    chatbotId,
    filterConfig,
    sortDirection,
    sortColumn,
    page,
    resultsPerPage,
    signal
  }: GetConversationsConfig): Promise<ConversationSearchResult> {
    const baseUrl = `chatbots/${chatbotId}/conversations`;

    //  Append search params from arguments
    const searchParams = new URLSearchParams();
    searchParams.append('chatbotId', chatbotId.toString());
    searchParams.append(
      'sortDirection',
      sortDirection === null ? '' : sortDirection
    );
    searchParams.append('sortColumn', sortColumn);
    searchParams.append('page', page.toString());
    searchParams.append('resultsPerPage', resultsPerPage.toString());
    AnalyticsService.conversationFilterParams(filterConfig).forEach(
      (value, key) => searchParams.append(key, value)
    );

    //  Retrieve data
    const apiResponse = await api.get(`${baseUrl}?${searchParams}`, { signal });
    const conversationSearchResult: ConversationSearchResult = apiResponse.data;
    return conversationSearchResult;
  }

  static async getPreviousNextConversation(
    chatbotId: number,
    currentChatId: string,
    sortDirection: SortDirection,
    sortColumn: SortColumn,
    firstMessageStartTime: string | null,
    firstMessageEndTime: string | null,
    signal: AbortSignal
  ): Promise<PreviousNextConversation> {
    const baseUrl = `chatbots/${chatbotId}/conversations/previous-next`;

    const searchParams = new URLSearchParams();
    searchParams.append('currentChatId', currentChatId);
    searchParams.append(
      'sortDirection',
      sortDirection === null ? '' : sortDirection
    );
    searchParams.append('sortColumn', sortColumn);

    if (firstMessageStartTime) {
      searchParams.append('firstMessageStartTime', firstMessageStartTime);
    }

    if (firstMessageEndTime) {
      searchParams.append('firstMessageEndTime', firstMessageEndTime);
    }

    const apiResponse = await api.get(`${baseUrl}?${searchParams}`, { signal });
    return apiResponse.data;
  }

  static async getIndividualConversation(
    chatbotId: number,
    conversationId: string,
    signal: AbortSignal
  ): Promise<IndividualConversation> {
    const baseUrl = `chatbots/${chatbotId}/conversations/${conversationId}`;

    const apiResponse = await api.get(baseUrl, { signal });
    const conversationSearchResult: IndividualConversation = apiResponse.data;
    return conversationSearchResult;
  }

  private static conversationFilterParams(
    filterConfig: ConversationFilterParams
  ): URLSearchParams {
    const {
      q,
      firstMessageStartTime,
      firstMessageEndTime,
      includeTest,
      issueReference
    } = filterConfig;

    const searchParams = new URLSearchParams();
    if (q) searchParams.append('q', q);
    if (firstMessageStartTime)
      searchParams.append('firstMessageStartTime', firstMessageStartTime);
    if (firstMessageEndTime)
      searchParams.append('firstMessageEndTime', firstMessageEndTime);
    if (includeTest !== undefined)
      searchParams.append('includeTest', includeTest.toString());
    if (issueReference) searchParams.append('issueReference', issueReference);
    return searchParams;
  }

  static async exportMessageHistory(
    chatbotId: number,
    filterConfig: ConversationFilterParams,
    exportFileType: ExportFileType
  ): Promise<void> {
    const queryString =
      AnalyticsService.conversationFilterParams(filterConfig).toString();
    const baseUrl = `chatbots/${chatbotId}/message-history/export/${exportFileType}`;

    const url = `${baseUrl}?${queryString}`;

    const apiResponse = await api.get(url, { responseType: 'blob' });

    const downloadUrl = window.URL.createObjectURL(
      new Blob([apiResponse.data])
    );
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute(
      'download',
      `chatbot-${chatbotId}-message-history.${exportFileType.toLowerCase()}`
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  static async exportAnalytics(
    chatbotId: number,
    params: URLSearchParams,
    exportFileType: ExportFileType
  ): Promise<void> {
    const queryString = params.toString();
    const baseUrl = `chatbots/${chatbotId}/analytics/export/${exportFileType}`;

    const url = `${baseUrl}?${queryString}`;

    const apiResponse = await api.get(url, { responseType: 'blob' });

    const downloadUrl = window.URL.createObjectURL(
      new Blob([apiResponse.data])
    );
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute(
      'download',
      `chatbot-${chatbotId}-analytics.${exportFileType.toLowerCase()}`
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  static async exportMissedUtteranceReport(
    chatbotId: number,
    startDate: string,
    endDate: string
  ): Promise<void> {
    const searchParams = new URLSearchParams();
    searchParams.append('startDate', startDate);
    searchParams.append('endDate', endDate);

    const url = `chatbots/${chatbotId}/missed-utterance-report/export/xlsx?${searchParams}`;

    const apiResponse = await api.get(url, { responseType: 'blob' });

    const contentDisposition =
      apiResponse.headers['content-disposition'] ||
      apiResponse.headers['Content-Disposition'];
    const matched = contentDisposition?.match(/filename="?([^";]+)"?/i);
    const filename =
      matched?.[1] || `chatbot-${chatbotId}-missed-utterance-report.xlsx`;

    const downloadUrl = window.URL.createObjectURL(
      new Blob([apiResponse.data])
    );
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  static async getIntents({
    chatbotId,
    query,
    page,
    resultsPerPage,
    signal
  }: GetIntentsConfig): Promise<IntentSearchResult> {
    const baseUrl = `chatbots/${chatbotId}/intents`;

    //  Append search params from arguments
    const searchParams = new URLSearchParams();
    searchParams.append('page', page.toString());
    searchParams.append('resultsPerPage', resultsPerPage.toString());
    searchParams.append('q', query);

    //  Retrieve data
    const apiResponse = await api.get(`${baseUrl}?${searchParams}`, { signal });
    const intentsSearchResult: IntentSearchResult = apiResponse.data;
    return intentsSearchResult;
  }

  static async rateMessage(
    chatbotId: number,
    messageId: number,
    rating: MessageRating
  ): Promise<MessageRatingResponse> {
    const baseUrl = `message-rating/${chatbotId}/${messageId}`;

    const reqBody = {
      rating
    };

    const apiResponse = await api.post(baseUrl, reqBody);
    return apiResponse.data;
  }

  static async undoRateMessage(
    chatbotId: number,
    messageId: number
  ): Promise<MessageRatingResponse> {
    const baseUrl = `message-rating/${chatbotId}/${messageId}`;

    const apiResponse = await api.post(baseUrl, { rating: null });
    return apiResponse.data;
  }

  static async getAnalytics(
    chatbotId: number | undefined,
    startDate: string,
    endDate: string,
    signal?: AbortSignal,
    includeTest?: boolean
  ): Promise<ChatbotAnalytics | null> {
    const baseUrl = `chatbots/${chatbotId}/analytics`;

    const searchParams = new URLSearchParams();
    searchParams.append('startDate', startDate);
    searchParams.append('endDate', endDate);
    if (includeTest !== undefined) {
      searchParams.append('includeTest', includeTest.toString());
    }

    const apiResponse = await api.get(`${baseUrl}?${searchParams}`, { signal });
    return apiResponse.data;
  }

  static async getEarliestAnalyticsDate(chatbotId: number): Promise<string> {
    const apiResponse = await api.get<{ earliestDate: string }>(
      `chatbots/${chatbotId}/analytics/earliest-date`
    );
    return apiResponse.data.earliestDate;
  }

  static async getReviewTags(): Promise<ReviewTag[]> {
    const apiResponse = await api.get<ReviewTag[]>('review-tags');
    return apiResponse.data;
  }

  static async updateMessageReviewTag(
    chatbotId: number,
    messageId: number,
    tagId: number | null
  ): Promise<ReviewTag | null> {
    const apiResponse = await api.post<ReviewTag | null>(
      `message-review/${chatbotId}/${messageId}/tags`,
      { tagId }
    );
    return apiResponse.data;
  }

  static async updateMessageReviewComment(
    chatbotId: number,
    messageId: number,
    commentText: string | null
  ): Promise<ReviewComment | null> {
    const apiResponse = await api.post<ReviewComment | null>(
      `message-review/${chatbotId}/${messageId}/comment`,
      { commentText }
    );
    return apiResponse.data;
  }

  static async getCommentSuggestions(query: string): Promise<string[]> {
    const searchParams = new URLSearchParams();
    searchParams.append('query', query);
    const apiResponse = await api.get<string[]>(
      `review-comments/suggestions?${searchParams}`
    );
    return apiResponse.data;
  }

  static async searchIssues(
    chatbotId: number,
    query: string
  ): Promise<Issue[]> {
    const searchParams = new URLSearchParams();
    searchParams.append('chatbotId', chatbotId.toString());
    searchParams.append('query', query);
    searchParams.append('size', '20');
    const apiResponse = await api.get<{ content: Issue[] }>(
      `issues/search?${searchParams}`
    );
    return apiResponse.data.content ?? [];
  }

  static async createIssue(
    scope: IssueScope,
    chatbotId: number | null,
    title: string,
    description: string
  ): Promise<Issue> {
    const apiResponse = await api.post<Issue>('issues', {
      scope,
      chatbotId,
      title,
      description
    });
    return apiResponse.data;
  }

  static async setMessageIssue(
    chatbotId: number,
    messageId: number,
    issueId: number | null
  ): Promise<Issue | null> {
    const apiResponse = await api.post<Issue | null>(
      `message-review/${chatbotId}/${messageId}/issue`,
      { issueId }
    );
    return apiResponse.data;
  }
}

export default AnalyticsService;
